/**
 * ContactViewModal - Модальное окно просмотра контакта
 */
import React from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Avatar,
  Badge,
  Divider,
  Box,
  Anchor,
  Button,
  CopyButton,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconStar,
  IconPhone,
  IconMail,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconBriefcase,
  IconCopy,
  IconCheck,
  IconPencil,
  IconUser,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';
import { formatPhoneDisplay, formatPhoneForLink } from '../../../utils/phoneUtils';

/**
 * Форматирование полного имени
 */
const formatFullName = (contact) => {
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  if (contact.middle_name) parts.push(contact.middle_name);
  return parts.join(' ') || 'Без имени';
};

/**
 * Получение инициалов
 */
const getInitials = (contact) => {
  const first = contact.first_name?.[0]?.toUpperCase() || '';
  const last = contact.last_name?.[0]?.toUpperCase() || '';
  return last + first || first || 'К';
};

/**
 * Компонент для отображения поля с возможностью копирования
 */
const ContactField = ({ icon: Icon, label, value, href, copyable = true }) => {
  if (!value) return null;

  return (
    <Group gap="sm" align="flex-start">
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: tokens.colors.neutral[2],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={tokens.colors.neutral[7]} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Group gap="xs">
          {href ? (
            <Anchor href={href} target={href.startsWith('http') ? '_blank' : undefined} size="sm">
              {value}
            </Anchor>
          ) : (
            <Text size="sm">{value}</Text>
          )}
          {copyable && (
            <CopyButton value={value} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Скопировано!' : 'Копировать'} withArrow>
                  <ActionIcon
                    variant="subtle"
                    color={copied ? 'green' : 'gray'}
                    size="xs"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          )}
        </Group>
      </Box>
    </Group>
  );
};

/**
 * Компонент для отображения телефона
 */
const PhoneField = ({ phone }) => {
  const typeLabel = phone.type === 'mobile' ? 'Мобильный' : 'Городской';
  const displayNumber = formatPhoneDisplay(phone.number);
  const linkNumber = formatPhoneForLink(phone.number);

  return (
    <ContactField
      icon={IconPhone}
      label={typeLabel}
      value={displayNumber}
      href={`tel:${linkNumber}`}
    />
  );
};

const ContactViewModal = ({
  opened,
  onClose,
  contact,
  onEdit,
}) => {
  if (!contact) return null;

  const fullName = formatFullName(contact);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Контакт"
      size="md"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="lg">
        {/* Заголовок с аватаром */}
        <Group>
          <Avatar size="xl" radius="xl" color="blue">
            {getInitials(contact)}
          </Avatar>
          <Box style={{ flex: 1 }}>
            <Group gap="xs">
              <Text size="lg" fw={600}>
                {fullName}
              </Text>
              {contact.is_primary && (
                <Badge
                  size="sm"
                  variant="filled"
                  radius="xl"
                  color="green"
                  leftSection={<IconStar size={10} />}
                >
                  Основной
                </Badge>
              )}
            </Group>
            {contact.position_name && (
              <Group gap={4} mt={4}>
                <IconBriefcase size={14} color={tokens.colors.neutral[6]} />
                <Text size="sm" c="dimmed">
                  {contact.position_name}
                </Text>
              </Group>
            )}
          </Box>
        </Group>

        <Divider />

        {/* Телефоны */}
        {contact.phones && contact.phones.length > 0 && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Телефоны
            </Text>
            {contact.phones.map((phone, index) => (
              <PhoneField key={index} phone={phone} />
            ))}
          </Stack>
        )}

        {/* Email */}
        {contact.email && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Email
            </Text>
            <ContactField
              icon={IconMail}
              label="Электронная почта"
              value={contact.email}
              href={`mailto:${contact.email}`}
            />
          </Stack>
        )}

        {/* Мессенджеры */}
        {contact.messengers && (contact.messengers.telegram || contact.messengers.whatsapp || contact.messengers.viber) && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Мессенджеры
            </Text>
            {contact.messengers.telegram && (
              <ContactField
                icon={IconBrandTelegram}
                label="Telegram"
                value={contact.messengers.telegram}
                href={
                  contact.messengers.telegram.startsWith('@')
                    ? `https://t.me/${contact.messengers.telegram.slice(1)}`
                    : `https://t.me/${contact.messengers.telegram}`
                }
              />
            )}
            {contact.messengers.whatsapp && (
              <ContactField
                icon={IconBrandWhatsapp}
                label="WhatsApp"
                value={contact.messengers.whatsapp}
                href={`https://wa.me/${contact.messengers.whatsapp.replace(/\D/g, '')}`}
              />
            )}
            {contact.messengers.viber && (
              <ContactField
                icon={IconUser}
                label="Viber"
                value={contact.messengers.viber}
                href={`viber://chat?number=${contact.messengers.viber.replace(/\D/g, '')}`}
              />
            )}
          </Stack>
        )}

        {/* Заметки */}
        {contact.notes && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Комментарии
            </Text>
            <Box
              p="sm"
              style={{
                backgroundColor: tokens.colors.neutral[1],
                borderRadius: '8px',
              }}
            >
              <Text size="sm">{contact.notes}</Text>
            </Box>
          </Stack>
        )}

        <Divider />

        {/* Кнопки действий */}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() => {
              onClose();
              onEdit(contact);
            }}
          >
            Редактировать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ContactViewModal;
